import { Check, Package, MapPin, Cube } from '@phosphor-icons/react'
import { UseFormWatch } from 'react-hook-form'
import { CreateQuoteData, INCOTERM_LABELS } from '../types/quote'
import { Modality } from '../types/quote'

interface QuoteSummaryProps {
  watch: UseFormWatch<CreateQuoteData>
  currentStep: number
  modalities: Modality[]
}

export function QuoteSummary({ watch, currentStep, modalities }: QuoteSummaryProps) {
  const modalityId = watch('modality_id')
  const incoterm = watch('incoterm')
  const clientReference = watch('client_reference')
  const origin = watch('origin')
  const destination = watch('destination')
  const volumeItems = watch('volume_items')
  const goodsDescription = watch('goods_description')

  const selectedModality = modalities.find(m => m.id === modalityId)

  const isStepComplete = (step: number) => currentStep > step

  return (
    <div className="w-80 bg-white rounded-lg border-2 border-gray-200 p-6 sticky top-4 h-fit">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
          <Package size={18} weight="duotone" className="text-emerald-600" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">Resumo da Cotação</h3>
          <p className="text-xs text-gray-500">Acompanhe seu progresso</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Step 1: Modality */}
        <div className={`p-3 rounded-lg transition-all ${
          isStepComplete(0)
            ? 'bg-emerald-50 border-2 border-emerald-200'
            : currentStep === 0
            ? 'bg-blue-50 border-2 border-blue-200'
            : 'bg-gray-50 border-2 border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {isStepComplete(0) ? (
                <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Check size={12} weight="bold" className="text-white" />
                </div>
              ) : (
                <div className={`w-5 h-5 rounded-full ${
                  currentStep === 0 ? 'bg-blue-500' : 'bg-gray-300'
                } flex items-center justify-center text-white text-xs font-bold`}>
                  1
                </div>
              )}
              <span className="text-sm font-semibold text-gray-900">Modalidade</span>
            </div>
          </div>

          {selectedModality ? (
            <div className="ml-7 space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Transporte:</span>
                <span className="text-xs font-medium text-gray-900 capitalize">
                  {selectedModality.name}
                </span>
              </div>
              {selectedModality.volume_name && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Tipo:</span>
                  <span className="text-xs font-medium text-gray-900">
                    {selectedModality.volume_name}
                  </span>
                </div>
              )}
              {incoterm && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Incoterm:</span>
                  <span className="text-xs font-medium text-gray-900">
                    {incoterm}
                  </span>
                </div>
              )}
              {clientReference && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Referência:</span>
                  <span className="text-xs font-medium text-gray-900">
                    {clientReference}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="ml-7 text-xs text-gray-400 italic">Aguardando...</p>
          )}
        </div>

        {/* Step 2: Route */}
        <div className={`p-3 rounded-lg transition-all ${
          isStepComplete(1)
            ? 'bg-emerald-50 border-2 border-emerald-200'
            : currentStep === 1
            ? 'bg-blue-50 border-2 border-blue-200'
            : 'bg-gray-50 border-2 border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {isStepComplete(1) ? (
                <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Check size={12} weight="bold" className="text-white" />
                </div>
              ) : (
                <div className={`w-5 h-5 rounded-full ${
                  currentStep === 1 ? 'bg-blue-500' : 'bg-gray-300'
                } flex items-center justify-center text-white text-xs font-bold`}>
                  2
                </div>
              )}
              <span className="text-sm font-semibold text-gray-900">Rota</span>
            </div>
          </div>

          {origin?.location?.city || destination?.location?.city ? (
            <div className="ml-7 space-y-2">
              {origin?.location?.city && (
                <div>
                  <span className="text-xs text-gray-500 block mb-0.5">Origem:</span>
                  <span className="text-xs font-medium text-gray-900">
                    {origin.location.city}, {origin.location.country}
                  </span>
                  {origin.contact_name && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      {origin.contact_name}
                    </div>
                  )}
                </div>
              )}
              {destination?.location?.city && (
                <div>
                  <span className="text-xs text-gray-500 block mb-0.5">Destino:</span>
                  <span className="text-xs font-medium text-gray-900">
                    {destination.location.city}, {destination.location.country}
                  </span>
                  {destination.contact_name && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      {destination.contact_name}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="ml-7 text-xs text-gray-400 italic">Aguardando...</p>
          )}
        </div>

        {/* Step 3: Cargo */}
        <div className={`p-3 rounded-lg transition-all ${
          isStepComplete(2)
            ? 'bg-emerald-50 border-2 border-emerald-200'
            : currentStep === 2
            ? 'bg-blue-50 border-2 border-blue-200'
            : 'bg-gray-50 border-2 border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {isStepComplete(2) ? (
                <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Check size={12} weight="bold" className="text-white" />
                </div>
              ) : (
                <div className={`w-5 h-5 rounded-full ${
                  currentStep === 2 ? 'bg-blue-500' : 'bg-gray-300'
                } flex items-center justify-center text-white text-xs font-bold`}>
                  3
                </div>
              )}
              <span className="text-sm font-semibold text-gray-900">Carga</span>
            </div>
          </div>

          {goodsDescription || (volumeItems && volumeItems.length > 0) ? (
            <div className="ml-7 space-y-2">
              {goodsDescription && (
                <div>
                  <span className="text-xs text-gray-500 block mb-0.5">Descrição:</span>
                  <span className="text-xs text-gray-700 line-clamp-2">
                    {goodsDescription}
                  </span>
                </div>
              )}
              {volumeItems && volumeItems.length > 0 && (
                <div>
                  <span className="text-xs text-gray-500 block mb-1">Volumes:</span>
                  <div className="space-y-1">
                    {volumeItems.map((item, index) => (
                      <div key={index} className="text-xs text-gray-700 flex items-center justify-between">
                        <span>{item.quantity}x {item.type}</span>
                        <span className="text-gray-500">
                          {item.weight} kg
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="ml-7 text-xs text-gray-400 italic">Aguardando...</p>
          )}
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">Progresso</span>
          <span className="text-xs font-bold text-emerald-600">
            {Math.round((currentStep / 3) * 100)}%
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-500"
            style={{ width: `${(currentStep / 3) * 100}%` }}
          />
        </div>
      </div>
    </div>
  )
}
